<template>
  <div class="card-update-tags" v-click-outside="deselectAll">
    <card title="Edit Tags">

      <p>
        <info-box>
          <ul>
            <li>Click on the Add Tag button to create a tag</li>
            <li>Reposition tags by clicking and dragging</li>
            <li>Click on a tag to display the edit tag dialog box</li>
          </ul>
        </info-box>
      </p>

      <button v-if="selectedSlide.label" class="btn btn-success my-2 mr-2" @click="addInteractiveSlideTag(selectedSlide._id)">add tag to slide</button>
      <button v-if="selectedSlide.label" class="btn btn-success my-2" @click="deselectSlide()"><fa :icon="['fas', 'times']" /></button>
      <button v-else class="btn btn-primary my-2" @click="addTag">add tag</button>

      <div class="tags-block">
        <div 
          class="canvas-wrapper"
          :class="{ 'canvas-wrapper--slide-selected' : isSlideSelected }"
          ref="canvasWrapper"
          @mousemove="handleDrag($event)"
          @mouseup.left.stop="handleDragEnd($event)"
          :style="canvasStyle"
        >
          <div class="slide-tag-layer">
            <template v-for="tag in selectedInteractiveSlideTags" tag="div">
              <tag-basic
                v-if="tag.type === 'basic'"
                :key="tag._id"
                v-bind="tag"
                :lang="lang"
                :class="{
                  active: selectedTag._id === tag._id,
                  inactive: selectedTag._id !== tag._id && selectedTag._id !== undefined
                }"
                @mousedown.native.left.stop="slideTagLayerDragStart(tag._id)"
                @mouseup.native.left.stop="slideTagLayerDragEnd($event, tag._id)"
              ></tag-basic>

              <tag-box
                v-if="tag.type === 'box'"
                :key="tag._id"
                v-bind="tag"
                :lang="lang"
                :class="{
                  active: selectedTag._id === tag._id, 
                  inactive: selectedTag._id !== tag._id && selectedTag._id !== undefined
                }"
                @mousedown.native.left.stop="slideTagLayerDragStart(tag._id)"
                @mouseup.native.left.stop="slideTagLayerDragEnd($event, tag._id)"
              ></tag-box>

              <tag-rich
                v-if="tag.type === 'rich'"
                :key="tag._id"
                v-bind="tag"
                :lang="lang"
                :dir="dir"
                :ref="tag._id"
                :class="{
                  active: selectedTag._id === tag._id, 
                  inactive: selectedTag._id !== tag._id && selectedTag._id !== undefined
                }"
                @mousedown.native.left.stop="slideTagLayerDragStart(tag._id)"
                @mouseup.native.left.stop="slideTagLayerDragEnd($event, tag._id)"
              ></tag-rich>

              <tag-trail
                v-if="tag.type === 'trail'"
                :key="tag._id"
                v-bind="tag"
                :lang="lang"
                :dir="dir"
                :ref="tag._id"
                :class="{
                  active: selectedTag._id === tag._id, 
                  inactive: selectedTag._id !== tag._id && selectedTag._id !== undefined
                }"
                @mousedown.native.left.stop="slideTagLayerDragStart(tag._id)"
                @mouseup.native.left.stop="slideTagLayerDragEnd($event, tag._id)"
              ></tag-trail>
            </template>
          </div>
          
          <div class="tag-layer" :class="{'tag-layer--inactive' : isSlideSelected}">
            <template v-for="tag in page.tags" tag="div">
              <tag-basic
                v-if="tag.type === 'basic'"
                :key="tag._id"
                v-bind="tag"
                :lang="lang"
                :class="{
                  active: selectedTag._id === tag._id, 
                  inactive: selectedTag._id !== tag._id && selectedTag._id !== undefined
                }"
                @mousedown.native.left.stop="handleDragStart($event, tag._id)"
                @mouseup.native.left.stop="handleDragEnd($event, tag._id)"
              ></tag-basic>

              <tag-box
                v-if="tag.type === 'box'"
                :key="tag._id"
                v-bind="tag"
                :lang="lang"
                :class="{
                  active: selectedTag._id === tag._id, 
                  inactive: selectedTag._id !== tag._id && selectedTag._id !== undefined
                }"
                @mousedown.native.left.stop="handleDragStart($event, tag._id)"
                @mouseup.native.left.stop="handleDragEnd($event, tag._id)"
              ></tag-box>

              <tag-reference
                v-if="tag.type === 'reference'"
                :key="tag._id"
                v-bind="tag"
                :lang="lang"
                :class="{
                  active: selectedTag._id === tag._id, 
                  inactive: selectedTag._id !== tag._id && selectedTag._id !== undefined
                }"
                :tagLabel="index + 1"
                @mousedown.native.left.stop="handleDragStart($event, tag._id)"
                @mouseup.native.left.stop="handleDragEnd($event, tag._id)"
              ></tag-reference>

              <tag-rich
                v-if="tag.type === 'rich'"
                :key="tag._id"
                v-bind="tag"
                :lang="lang"
                :dir="dir"
                :ref="tag._id"
                :class="{
                  active: selectedTag._id === tag._id, 
                  inactive: selectedTag._id !== tag._id && selectedTag._id !== undefined
                }"
                @mousedown.native.left.stop="handleDragStart($event, tag._id)"
                @mouseup.native.left.stop="handleDragEnd($event, tag._id)"
              ></tag-rich>

              <tag-trail
                v-if="tag.type === 'trail'"
                :key="tag._id"
                v-bind="tag"
                :lang="lang"
                :dir="dir"
                :ref="tag._id"
                :class="{
                  active: selectedTag._id === tag._id, 
                  inactive: selectedTag._id !== tag._id && selectedTag._id !== undefined
                }"
                @mousedown.native.left.stop="handleDragStart($event, tag._id)"
                @mouseup.native.left.stop="handleDragEnd($event, tag._id)"
              ></tag-trail>

              <tag-feature-text
                v-if="tag.type === 'feature'"
                :key="tag._id"
                v-bind="tag"
                :lang="lang"
                :class="{
                  active: selectedTag._id === tag._id, 
                  inactive: selectedTag._id !== tag._id && selectedTag._id !== undefined
                }"
                @mousedown.native.left.stop="handleDragStart($event, tag._id)"
                @mouseup.native.left.stop="handleDragEnd($event, tag._id)"
              ></tag-feature-text>
            </template>
          </div>

          <reference-tag-slider 
            v-if="referenceTagsSlider.length > 0"
            :key="referenceTagSliderKey"
            :lang="lang"
            :dir="dir"
            :isVertical="page.verticalSlider"
            :scaledAmount="scale"
            :tagRef="referenceTagsSlider"
          ></reference-tag-slider>

          <div class="interactive-slides">
            <draggable
              tag="div"
              class="base-items-inner d-flex"
              v-model="interactiveSlides"
              v-bind="dragOptionsSlides"
              :group="{pull: false}"
              :key="selectedSlide._id"
            >
              <div
                v-for="slide in interactiveSlides"
                :key="slide._id"
                class="interactive-slides__slide"
                :class="[{'interactive-slides__slide--no-bg' : slide.asset.isEmpty}, {'interactive-slides__slide--inactive' : slide._id !== selectedSlide._id}]"
                @click="editInteractiveSlide(slide._id)"
              >
                <div class="interactive-slides__label" v-html="slide.label[lang]"></div>
                <img
                  v-if="!slide.asset.isEmpty"
                  :src="`${$store.state.assetsPath}${slide.asset.customThumbPath ? slide.asset.customThumbPath : slide.asset.thumbPath}`"
                  alt="">
              </div>
            </draggable>
            <button class="interactive-slides__slide interactive-slides__slide--add-btn" @click="addInteractiveSlide">add slide</button>
          </div>

          <div ref="canvas" class="canvas" :class="{'no-image' : page.backgroundImage === ''}">
            <img
              v-if="backgroundAssetType === 'image' || slideBgOverride"
              class="tagBg"
              :class="{'hidden' : page.backgroundImage === ''}"
              :src="`${$store.state.assetsPath}${slideBgOverride ? slideBgOverride : page.backgroundImage}`"
            >
            <video
              v-if="backgroundAssetType === 'video' && !slideBgOverride"
              class="tagBg"
              :class="{'hidden' : page.backgroundImage === ''}"
              :src="`${$store.state.assetsPath + page.backgroundImage}`"
              autoplay
              muted
              loop
            ></video>
          </div>
        </div>

        <tag-info-box v-model="selectedTag" :lang="lang" @remove="removeTag" :scale="scale" />
        <tag-info-box v-if="selectedSlide._id && !selectedTag._id" v-model="selectedSlide" :lang="lang" @remove="removeSlide" :scale="scale" />
      </div>
    </card>
  </div>
</template>

<script>
import InfoBox from '@/components/InfoBox'
import TagBasic from '@/components/TagBasic'
import TagBox from '@/components/TagBox'
import TagFeatureText from '@/components/TagFeatureText'
import TagRich from '@/components/TagRich'
import TagReference from '@/components/TagReference'
import TagTrail from '@/components/TagTrail'
import TagInfoBox from '@/components/TagInfoBox'
import ReferenceTagSlider from '@/components/ReferenceTagSlider'
import draggable from 'vuedraggable'

export default {
  name: 'card-update-interactive-tags',

  components: {
    draggable,
    InfoBox,
    ReferenceTagSlider,
    TagBasic,
    TagBox,
    TagFeatureText,
    TagRich,
    TagReference,
    TagTrail,
    TagInfoBox
  },

  props: ['dir', 'lang', 'page', 'triggerSliderRefresh'],

  data() {
    return {
      draggingTag: {},
      isSlideSelected: false,
      isTagDraggable: false,
      isTagDragging: false,
      scale: 1,
      selectedSlide: {},
      selectedTag: {},
      slideBgOverride: null
    }
  },

  computed: {
    dragOptionsSlides() {
      return {
        animation: 120,
        group: "slides",
        disabled: false,
        ghostClass: "ghostly"
      };
    },

    backgroundAssetType () {
      if (this.page.backgroundImage.split('.').pop() === 'mp4') {
        return 'video'
      } else {
        return 'image'
      }
    },

    canvasStyle () {
      if (this.scale) {
        return {
          ['font-size']: (20 * this.scale) + 'px',
          ['height']: (1960 * this.scale) + 'px',
        }
      }
      return {}
    },

    highestId () {
      let ids = [0]

      this.page.tags.forEach((tag) => {
        if (!isNaN(tag._id)) {
          ids.push(tag._id)
        }        
      })

      this.page.interactiveSlides.forEach(slide => {
        if (!isNaN(slide._id)) {
          ids.push(slide._id)
        }        
      })

      this.page.interactiveSlides.forEach(slide => {
        slide.tags.forEach(tag => {
          if (!isNaN(tag._id)) {
            ids.push(tag._id)
          }  
        }) 
      })

      return (Math.max(...ids) + 1).toString()
    },

    referenceTagSliderKey () {
      return this.tagRef.length + '-' + this.page.verticalSlider + '-' + this.scale + '-' + this.triggerSliderRefresh
    },

    interactiveSlides: {
      get: function () {
        return this.page.interactiveSlides
      },

      set: function (newValue) {
        this.page.interactiveSlides = newValue
      }
    },

    selectedInteractiveSlideTags () {
      const selectedSlideId = this.selectedSlide._id
      let slide = _.find(this.page.interactiveSlides, slide => {
        return slide._id === selectedSlideId
      })

      if (slide !== undefined) {
        return slide.tags
      } else {
        return []
      }
    },

    referenceTagsSlider () {
      let refTags = this.page.tags.filter(function(tag) {
        return tag.type == "reference"
      })

      refTags = _.orderBy(refTags, ['pos.x'], ['asc'])

      if (this.dir === 'rtl') {
        let startingIndex = refTags.length

        refTags.forEach((tag, index) => {
          if (!tag.hideTagReference) {
            tag.referenceIndex = startingIndex--
          }
        })
        return refTags
      } else {
        refTags.forEach((tag, index) => {
          if (!tag.hideTagReference) {
            tag.referenceIndex = index + 1
          }
        })
        return refTags
      }
    }
  },

  mounted() {
    window.addEventListener('resize', this.getScaledAmount)
    this.getScaledAmount()
  },

  methods: {
    getScaledAmount() {
      // default size based on 3040x1960 - scale = 1
      let canvasWidth = this.$refs.canvasWrapper.getBoundingClientRect().width
      let scaled = canvasWidth / 3190
      this.scale = scaled
    },

    handleDragStart(event, tagId) {
      this.isTagDraggable = true

      let tag = _.find(this.page.tags, tag => {
        return tag._id === tagId
      })

      if (tag === undefined) {
        tag = _.find(this.page.interactiveSlides, tag => {
          return tag._id === tagId
        })
      }

      this.draggingTag = tag
      this.selectedTag = tag
    },

    handleDrag(event) {
      // check if a tag is being dragged
      if (this.isTagDraggable) {
        this.isTagDragging = true

        const canvasWidth = this.$refs.canvas.offsetWidth
        const canvasHeight = this.$refs.canvas.offsetHeight

        // get tag element
        const draggingTagId = this.draggingTag._id

        let tag = _.find(this.page.tags, tag => {
          return tag._id === draggingTagId
        })

        if (tag === undefined) {
          tag = _.find(this.selectedInteractiveSlideTags, tag => {
            return tag._id === draggingTagId
          })
        }

        const tagEl = document.getElementById(draggingTagId).getBoundingClientRect()
        const tagElWidth = tagEl.width
        const tagElHeight = tagEl.height

        tag.pos.x = Math.round(tag.pos.x + event.movementX / this.scale)
        tag.pos.y = Math.round(tag.pos.y + event.movementY / this.scale)

        if (tag.pos.x < 0) {
          tag.pos.x = 0
        }

        if (tag.pos.x > canvasWidth / this.scale) {
          tag.pos.x = Math.round(canvasWidth / this.scale)
        }

        if (tag.pos.y < 0) {
          tag.pos.y = 0
        }

        if (tag.pos.y > canvasHeight / this.scale) {
          tag.pos.y = Math.round(canvasHeight / this.scale)
        }
      }
    },

    handleDragEnd(event, tagId) {
      let target = event.target.classList[0]


      if (target === 'canvas' || target === 'interactive-slides__slide') {
        this.isTagSelected = false
        this.selectedTag = {}
      } else {
        this.showPopupModal('tag', tagId)
      }

      this.isTagDragging = false
      this.isTagDraggable = false
    },

    addInteractiveSlide () {
      const newSlide = {
        _id: this.highestId,
        asset: { isEmpty: true },
        assetId: null,
        backgroundImageOverride: '',
        label: {
          en: ''
        },
        tags: []
      }

      this.page.interactiveSlides.push(newSlide)
    },

    editInteractiveSlide (id) {
      this.showPopupModal('slide', id)
    },

    addInteractiveSlideTag (id) {
      const newTag = {
        _id: this.highestId,
        arrowDeg: '',
        description: {
          en: ''
        },
        distance: {
          en: ''
        },
        icon: '',
        image: '',
        label: {
          en: 'New Tag'
        },
        flip: false,
        moreInformation: {
          en: ''
        },
        pos: {
          x: 100,
          y: 100,
        },
        tailPosition: 'bottom-center',
        type: 'basic'
      }

      // get interactive slide
      let slide = _.find(this.page.interactiveSlides, slide => {
        return slide._id === id
      })

      slide.tags.push(newTag)
    },

    addTag() {
      const newTag = {
        _id: this.highestId,
        arrowDeg: '',
        description: {
          en: ''
        },
        distance: {
          en: ''
        },
        icon: '',
        image: '',
        label: {
          en: 'New Tag'
        },
        flip: false,
        moreInformation: {
          en: ''
        },
        pos: {
          x: 100,
          y: 100,
        },
        tailPosition: 'bottom-center',
        type: 'basic'
      }

      this.page.tags.push(newTag)
    },

    deselectAll () {
      this.deselectSlide()
      this.deselectTag()
    },

    deselectSlide () {
      this.isSlideSelected = false
      this.slideBgOverride = null
      this.selectedSlide = {}
    },

    deselectTag () {
      this.isTagSelected = false
      this.selectedTag = {}
    },

    removeSlide () {
      let selectedSlideId = this.selectedSlide._id;
      this.selectedSlide = {}
      this.isSlideSelected = false

      let remainingInteractiveSlides = this.page.interactiveSlides.filter(function(slide) {
        if (slide._id != selectedSlideId) {
          return slide
        }
      })

      this.page.interactiveSlides = remainingInteractiveSlides
    },

    removeTag () {
      const selectedTagId = this.selectedTag._id;

      // remove tag from page
      this.selectedTag = {}

      let remainingTags = this.page.tags.filter(function(tag) {
        if (tag._id != selectedTagId) {
          return tag
        }
      })

      this.page.tags = remainingTags

      // remove tag from slide
      let selectedSlideId = this.selectedSlide._id
      let remainingSlideTags = this.selectedInteractiveSlideTags.filter(function(tag) {
        if (tag._id != selectedTagId) {
          return tag
        }
      })

      _.find(this.page.interactiveSlides, slide => {
        if (slide._id === selectedSlideId) {
          slide.tags = remainingSlideTags
        }
      })
    },

    showPopupModal(type, id) {
      let pageElement = null

      if (type === 'tag') {
        pageElement = _.find(this.page.tags, tag => {
          return tag._id === id
        })

        this.selectedSlide = {}
        this.selectedTag = pageElement
        this.isSlideSelected = false
      }

      if (type === 'slide') {
        pageElement = _.find(this.page.interactiveSlides, slide => {
          return slide._id === id
        })

        pageElement.type = 'slide'

        // deselect slide if clicking on selected slide
        if (pageElement._id === this.selectedSlide._id) {
          this.isSlideSelected = false
          this.slideBgOverride = null
          this.selectedSlide = {}
        } else {
          this.isSlideSelected = true
          this.slideBgOverride = pageElement.backgroundImageOverride
          this.selectedSlide = pageElement
        }

        this.selectedTag = {}
      }

      if (type === 'slidetag') {
        pageElement = _.find(this.selectedInteractiveSlideTags, tag => {
          return tag._id === id
        })

        this.selectedTag = pageElement
      }
    },

    openAssetLibrary(context, assetSelected) {
      this.$emit('openAssetLibrary', context, assetSelected)
    },

    slideTagLayerDragStart (tagId) {
      this.isTagDraggable = true

      let tag = _.find(this.selectedInteractiveSlideTags, tag => {
        return tag._id === tagId
      })

      this.draggingTag = tag
      this.selectedTag = tag
    },

    slideTagLayerDragEnd (event, tagId) {
      let target = event.target.getAttribute('class')

      if (target === 'canvas-wrapper' || target === 'canvas' || target === 'canvas no-image') {
        this.isTagSelected = false
        this.selectedTag = {}
      } else {
        this.showPopupModal('slidetag', tagId)
      }

      this.isTagDragging = false
      this.isTagDraggable = false
    }
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.getScaledAmount)
  }
}
</script>

<style lang="scss">
@import '@/styles/_variables.scss';
@import '@/styles/_mixins.scss';

.tags-block {
  position: relative;
}

.canvas {
  background-position: 50% 50%;
  background-size: cover;
  height: 100%;
  position: absolute;
  user-select: none;
  width: 100%;
  z-index: 1;

  &.no-image {
    background-image: linear-gradient(45deg, #eee 25%, transparent 25%, transparent 75%, #eee 75%, #eee 100%), linear-gradient(45deg, #eee 25%, white 25%, white 75%, #eee 75%, #eee 100%);
    background-position: 0px 0px, 10px 10px;
    background-size: 20px 20px;
  }

  .tagBg {
    height: 100%;
    object-fit: cover;
    pointer-events: none;
    width: 100%;

    &.hidden {
      opacity: 0;
    }
  }
}

.canvas-wrapper {
  margin: 20px 0 5px;
  position: relative;
  width: 100%;

  @include breakpoint(1800px) {
    width: 80%;
  }

  &--slide-selected {
    outline: 5px solid #28a745;
  }
}

.card-update-tags {
  padding-right: 15px;
  padding-left: 15px;
  width: 100%;
}

.tag-anchor,
.tag-box,
.tag-rich,
.tag-reference {
  transition: opacity 200ms ease-in-out, box-shadow 200ms ease-in-out;
  z-index: 20;

  &.inactive {
    box-shadow: none;
    opacity: .7;
    z-index: 10;

    .tag-anchor__pos {

      .tag-rich,
      .tag-basic {
        box-shadow: none;
        opacity: .7;
        z-index: 10;
      }

      &:after {
        opacity: .7;
      }
    }
  }
}

.interactive-slides {
  align-items: flex-end;
  bottom: 5em;
  display: flex;
  justify-content: center;
  pointer-events: none;
  position: absolute;
  width: 100%;
  z-index: 20;

  &__slide {
    background: transparent;
    border-radius: .5em;
    box-shadow: $tag-box-shadow;
    display: inline-block;
    color: #ffffff;
    cursor: pointer;
    height: 8.25em;
    margin: 0 1em;
    overflow: hidden;
    pointer-events: auto;
    position: relative;
    transition: opacity 200ms ease;
    user-select: none;
    width: 12.5em;

    &--add-btn {
      appearance: none;
      background: transparent;
      box-shadow: none;
      border: .2em dashed rgba(255, 255, 255, .8);
      color: #ffffff;
    }

    &--no-bg {
      background-position: 0px 0px, 10px 10px;
      background-size: 20px 20px;
      background-image: 
      linear-gradient(45deg, #eee 25%, transparent 25%, transparent 75%, #eee 75%, #eee 100%),
      linear-gradient(45deg, #eee 25%, white 25%, white 75%, #eee 75%, #eee 100%);
    }

    &--inactive {
      opacity: .5;
    }

    img {
      height: 100%;
      object-fit: cover;
      pointer-events: none;
      width: 100%;
    }
  }

  &__label {
    align-items: center;
    background: rgba(0, 0, 0, .6);
    bottom: 0;
    display: flex;
    font-size: .6em;
    font-weight: 300;
    justify-content: center;
    height: 2.1em;
    letter-spacing: .05em;
    padding: .2em .75em;
    position: absolute;
    text-transform: uppercase;
    width: 100%;
  }
}

.slide-tag-layer {
  height: 100%;
  pointer-events: none;
  position: absolute;
  width: 100%;
  z-index: 15;
}

.tag-layer {
  height: 100%;
  pointer-events: none;
  position: absolute;
  width: 100%;
  z-index: 10;

  &--inactive {
    opacity: .5;
  }
}
</style>
